import { useState, useRef, useEffect } from 'react';
import Page from '../components/Page';
import { AnimatePresence, motion } from 'framer-motion';
import { useAppStore } from '../stores/useAppStore';
import ExpandButton from '../components/ExpandButton';

const MotionExpandButton = motion.create(ExpandButton);

const pageVariants = {
  normal: {
    paddingTop: '8.375rem',
    paddingBottom: '13.25rem',
    paddingLeft: '0rem',
    paddingRight: '32rem',
    transition: { duration: 0.3, ease: "easeInOut" },
  },
  fullScreen: {
    paddingTop: '3.875rem',
    paddingBottom: '3.875rem',
    paddingLeft: '4.25rem',
    paddingRight: '4.25rem',
    transition: { duration: 0.3, ease: "easeInOut" },
  },
};

const expandButtonAnimation = {
    initial: { x: "+200%", opacity: 0 },
    animate: { x: 0, opacity: 1 },
    exit: { x: "+200%", opacity: 0 },
};

export function PageAIEra() {
  const { fullScreen, toggleFullScreen, hideHeader, setHideHeader } = useAppStore();

  // Video playback states
  const videoRef = useRef(null);
  const [showOverlay, setShowOverlay] = useState(true);
  const [isLoopMode, setIsLoopMode] = useState(true);
  const loopDuration = 20; // segundos
/*
  const handleClick = () => {
    setHideHeader(!hideHeader);
    toggleFullScreen();
  };
*/
  const handlePlayClick = () => {
    setHideHeader(!hideHeader);
    toggleFullScreen();

    setShowOverlay(false);
    setIsLoopMode(false);
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.muted = false;
      videoRef.current.play();
    }
  };

  const handleTimeUpdate = () => {
    if (isLoopMode && videoRef.current && videoRef.current.currentTime >= loopDuration) {
      videoRef.current.currentTime = 0;
    }
  };

  const handleEnded = () => {
    // Video completo terminado
    setShowOverlay(true);
    setTimeout(() => {
        if (fullScreen) {
          setHideHeader(!hideHeader);
          toggleFullScreen();
        }
      setIsLoopMode(true);
      if (videoRef.current) {
        videoRef.current.muted = true;
        videoRef.current.currentTime = 0;
        videoRef.current.play();
      }
    }, 100);
  };

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    
    if (isLoopMode) {
      v.muted = true;
      v.play();
    }
  }, [isLoopMode]);

  useEffect(() => {
    // Reset video state when exiting full screen
    if (!fullScreen) {
      setShowOverlay(true);
      setIsLoopMode(true);
      if (videoRef.current) {
        videoRef.current.muted = true;
        videoRef.current.currentTime = 0;
        videoRef.current.play();
      }
    }
  }, [fullScreen]);

  return (
    <Page>
      <motion.div
        initial="normal"
        variants={pageVariants}
        animate={fullScreen ? "fullScreen" : "normal"}
        className="flex w-full h-full transform-gpu"
      >
        <div
          className="w-full rounded-[3.75rem] transform-gpu bg-[#0E2242]
          relative overflow-hidden"
        >
            <video
                ref={videoRef}
                className="absolute inset-0 w-full h-full object-cover"
                src={`${process.env.PUBLIC_URL}/ciscovideo-kiosk.mp4`}
                autoPlay
                muted
                playsInline
                onTimeUpdate={handleTimeUpdate}
                onEnded={handleEnded}
            />
            <AnimatePresence>
              {isLoopMode && (
                <motion.div 
                  className="absolute inset-0 bg-[#0E2242A6] bg-opacity-65"
                  initial={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                />
              )}
            </AnimatePresence>
            {showOverlay && (
                <motion.div 
                    className="absolute z-10 inset-0 flex flex-col items-center pt-[20.125rem] pb-[21.375rem] px-[44.125rem]"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 0.5 }}
                >
                    <div className='text-[5.625rem] leading-[7.5rem] font-extralight text-center mb-[2rem]'>The critical infrastructure for the AI era.</div>
                    <div className='text-[2.625rem] leading-[4.25rem] font-extralight text-center mb-[4rem]'>We powered every major technology transformation over the past four decades. Now, we help organizations connect, protect, and thrive in an AI-driven world.</div>
                    <motion.div
                        onClick={handlePlayClick}
                        className="w-[14.75rem] h-[14.75rem] bg-white bg-opacity-10 rounded-full relative origin-center"
                        whileTap={{ scale: 0.9 }}
                        transition={{ type: 'spring', stiffness: 400 }}
                    >
                        <div className="absolute inset-[1.5rem] bg-white bg-opacity-40 rounded-full flex items-center justify-center">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="3.8125rem"
                                height="4.8125rem"
                                fill="none"
                                viewBox="0 0 57 70"
                                className='ml-[1rem]'
                            >
                                <path
                                fill="#fff"
                                d="M0 5.26v58.86c0 3.898 4.26 6.297 7.592 4.276l46.631-28.274c3.144-1.907 3.224-6.44.15-8.456L7.743 1.079C4.416-1.103 0 1.283 0 5.259"
                                ></path>
                            </svg>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </div>
        <AnimatePresence initial={false}>
          {!fullScreen && (
            <motion.div
              className="absolute right-0 top-[30.1875rem] w-[26.375rem] h-[46.875rem] flex flex-col bg-[#0E2242] border-[0.1875rem] border-[#B0B3C1] rounded-l-[2.5rem] px-[5.75rem] pt-[6.3125rem]"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
            >
              <div className="text-[2.625rem] leading-[4.25rem] font-extralight mb-[3rem]">
                Scan the QR code to explore our full AI story.
              </div>
              <img className="w-[14rem] h-auto rounded-[1.25rem]" src={`${process.env.PUBLIC_URL}/qr_ai_era.png`} />
            </motion.div>
          )}
        </AnimatePresence>
        {/*
        <AnimatePresence initial={false}>
          {!fullScreen && (
            <MotionExpandButton
              initial={expandButtonAnimation.initial}
              animate={expandButtonAnimation.animate}
              exit={expandButtonAnimation.exit}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              onClick={handleClick}
              className="absolute bottom-[13.125rem] right-[19.5rem] w-[6.875rem] h-[6.875rem] focus:outline-none"
            />
          )}
        </AnimatePresence>
        */}
      </motion.div>
    </Page>
  );
}
